import { ArrowRight } from "lucide-react";
import AttachIcon from "../ui/icons/AttachIcon";
import LockIcon from "../ui/icons/LockIcon";
import SourceSelectPopover from "@/components/ai/source-select-popover";
import AttachBox from "@/components/ai/attach-box";
import JobTag from "@/components/ai/job-tag";
import { useAiAgentStore } from "@/stores/ai-agent";
import { useRef, useEffect, useState } from "react";
import { formatFileSize, getFileType, checkFileExtensions } from "@/utils/file-upload";
import { useStopChatMutation } from "@/hooks/services/mutations/ai-agent/use-stop-chat-mutation";
import Spinner from "../shared/spinner";
import { toast } from "sonner";
import CustomToast from "../shared/custom-toast";
import useUserQuery from "@/hooks/services/queries/user/use-user-query";
import useChatStreamActiveQuery from "@/hooks/services/queries/ai-agent/use-chat-stream-active-query";
import { useChatsQuery } from "@/hooks/services/queries/ai-agent/use-chats-query";

const allowedExtensions = ["pdf"];

const MAX_CONTAINER_HEIGHT = 450;
const FIXED_AREA_HEIGHT = 80;
const MAX_TEXTAREA_HEIGHT = MAX_CONTAINER_HEIGHT - FIXED_AREA_HEIGHT;

const glassStyle = {
  background: "#FFFFFF80",
  boxShadow: "0px 4px 10px 0px #3737370D",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
} as React.CSSProperties;

const MessageInputMedium = ({
  onSend,
  chatId,
  expanded,
  onExpand,
  autoFocus = false,
}: {
  onSend: (text: string, files?: File[]) => void;
  chatId: string;
  expanded: boolean;
  onExpand: () => void;
  autoFocus?: boolean;
}) => {
  const {
    attachments,
    setAttachments,
    removeAttachment,
    message,
    setMessage,
    selectedJob,
    clearSelectedJob,
  } = useAiAgentStore();

  const setEventType = useAiAgentStore((state) => state.setEventType);
  const setProgressList = useAiAgentStore((state) => state.setProgressList);
  const streamingChatIds = useAiAgentStore((state) => state.streamingChatIds);
  const showFileUploadError = useAiAgentStore((state) => state.showFileUploadError);
  const setShowFileUploadError = useAiAgentStore((state) => state.setShowFileUploadError);

  const { mutate: stopChat, isPending: isStopChatPending } = useStopChatMutation();
  const { data: userData } = useUserQuery();
  const { data: chatsData } = useChatsQuery();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isSending, setIsSending] = useState(false);

  const isThisChatStreaming = streamingChatIds.has(chatId);
  const { data: streamActiveData } = useChatStreamActiveQuery(chatId, !isThisChatStreaming);
  const isServerStreaming = streamActiveData?.active === true;
  const hasNoCredit = userData?.role === 1 && (userData?.availableCredit ?? 0) < 1;
  const isNewChat = !chatId || chatId.startsWith("temp");
  const disableDueToCredit = hasNoCredit && !isNewChat;
  // 타인의 채팅방 여부: 채팅 목록의 userId와 내 id가 다르면 입력 불가
  const currentChat = chatsData?.pages.flatMap((p) => p.content).find((c) => c.chatId === chatId);
  const chatUserId = currentChat?.userId;
  const myId = userData?.id;
  const isOtherUserChat =
    chatUserId != null &&
    chatUserId !== "" &&
    myId != null &&
    String(chatUserId) !== String(myId);
  const isDisabled = isThisChatStreaming || isServerStreaming || disableDueToCredit || isOtherUserChat;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    if (checkFileExtensions(files, allowedExtensions)) {
      toast.custom(
        () => <CustomToast type="error" text="Unsupported file format(PDF only)." />,
        { position: "bottom-center" }
      );
      event.target.value = "";
      return;
    }

    const newAttachments = Array.from(files).map((file, index) => ({
      id: Date.now() + index,
      s3Url: URL.createObjectURL(file),
      originalName: file.name,
      filesize: formatFileSize(file.size),
      type: getFileType(file),
      fileName: file.name,
      imageUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : "",
      file,
    }));

    setAttachments([...attachments, ...newAttachments]);
    event.target.value = "";
    if (!expanded) onExpand();
  };

  const handleSendMessage = async () => {
    const trimmedMessage = message.trim();
    if (isSending || !trimmedMessage) return;

    setIsSending(true);
    const messageToSend = trimmedMessage;
    setMessage("");

    const files = attachments
      .map((attachment) => attachment.file)
      .filter((file): file is File => file !== undefined);

    try {
      onSend(messageToSend, files.length > 0 ? files : undefined);
      setAttachments([]);
      clearSelectedJob();
    } catch (error) {
      console.error("Failed to send message:", error);
      setMessage(messageToSend);
    } finally {
      setIsSending(false);
    }
  };

  const handleSendLight = (e: React.MouseEvent) => {
    e.stopPropagation();
    const trimmed = message.trim();
    if (!trimmed) return;
    const files = attachments
      .map((a) => a.file)
      .filter((f): f is File => f !== undefined);
    onSend(trimmed, files.length > 0 ? files : undefined);
    setMessage("");
    setAttachments([]);
    clearSelectedJob();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.nativeEvent.isComposing) return;
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleTextareaClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).tagName === "TEXTAREA") return;
    textareaRef.current?.focus();
    textareaRef.current?.setSelectionRange(message.length, message.length);
  };

  const adjustTextareaHeight = () => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = "auto";
    textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [message]);

  useEffect(() => {
    return () => {
      setEventType("idle");
      setProgressList([]);
    };
  }, [setEventType, setProgressList]);

  useEffect(() => {
    if (showFileUploadError) {
      toast.custom(
        () => <CustomToast type="error" text="File upload failed. Please try again." />,
        { position: "bottom-center" }
      );
      setShowFileUploadError(false);
    }
  }, [showFileUploadError, setShowFileUploadError]);

  const containerStyle = {
    background: "#FFFFFFF2",
    border: "1px solid #EFFFFC",
    boxShadow: "0px 4px 10px 0px #DFF4F0",
  } as React.CSSProperties;

  const icons = (
    <div className="flex items-center gap-2 shrink-0">
      <label
        className={`${isDisabled ? "cursor-not-allowed" : "cursor-pointer"} w-[40px] h-[40px] flex items-center justify-center rounded-[8px]`}
        style={glassStyle}
      >
        <input
          type="file"
          hidden
          multiple
          accept={allowedExtensions.map((ext) => `.${ext}`).join(",")}
          onChange={handleFileChange}
          disabled={isDisabled}
        />
        <AttachIcon color={isDisabled ? "#B3B3B3" : "#4D4D4D"} />
      </label>
      <div className="rounded-[8px]" style={glassStyle}>
        <SourceSelectPopover
          chatId={chatId || "temp"}
          disabled={isDisabled}
        />
      </div>
    </div>
  );

  const sendButton = isOtherUserChat ? (
    <button
      disabled
      className="bg-primary-50 rounded-full text-white w-[28px] h-[28px] flex items-center justify-center cursor-not-allowed shrink-0"
    >
      <LockIcon />
    </button>
  ) : (
    <button
      className="bg-primary-50 rounded-full text-white w-[28px] h-[28px] flex items-center justify-center hover:bg-primary-60 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
      disabled={!message.trim() || isDisabled}
      onClick={expanded ? handleSendMessage : handleSendLight}
    >
      <ArrowRight size={16} />
    </button>
  );

  if (!expanded) {
    return (
      <div
        className="w-full max-w-[724px] rounded-[16px] cursor-text overflow-hidden"
        style={containerStyle}
        onClick={onExpand}
      >
        {selectedJob != null && (
          <div
            className="px-4 pt-3 pb-1"
            onClick={(e) => e.stopPropagation()}
          >
            <JobTag
              job={selectedJob.analysisNo}
              onRemove={() => clearSelectedJob()}
            />
          </div>
        )}
        <div className="flex items-center gap-6 px-4 h-[56px]">
          <div
            className="flex items-center gap-2 shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            {icons}
          </div>
          <div
            className="flex-1 text-[16px] truncate select-none"
            style={{
              color: message ? "#1F1F1F" : "#9E9E9E",
              fontWeight: 400,
            }}
          >
            {message || (isOtherUserChat ? "Only the owner can send messages." : "Ask CellKey AI")}
          </div>
          <div onClick={(e) => e.stopPropagation()}>
            {!isOtherUserChat && (isThisChatStreaming || isServerStreaming) ? (
              <button
                onClick={() => stopChat(chatId)}
                className="bg-[#FFEAEC] rounded-full text-white w-[28px] h-[28px] flex items-center justify-center hover:bg-primary-60 transition-colors"
              >
                <div className="rounded-[2px] bg-red-30 w-3 h-3" />
              </button>
            ) : (
              sendButton
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative w-full max-w-[724px] rounded-[16px] flex flex-col overflow-hidden"
      style={containerStyle}
    >
      {attachments?.length > 0 && (
        <div className="px-5 pt-3 flex gap-[10px] shrink-0">
          {attachments.map((attachment) => (
            <AttachBox
              key={attachment.id}
              {...attachment}
              onRemove={removeAttachment}
            />
          ))}
        </div>
      )}

      {selectedJob != null && (
        <div className="px-5 pt-3 pb-1 shrink-0">
          <JobTag
            job={selectedJob.analysisNo}
            onRemove={() => clearSelectedJob()}
          />
        </div>
      )}

      <div
        className="px-5 pt-4 pb-2 cursor-text flex flex-wrap items-start gap-1"
        style={{ minHeight: "60px" }}
        onClick={handleTextareaClick}
      >
        <textarea
          ref={textareaRef}
          placeholder={isOtherUserChat ? "Only the owner can send messages." : "Ask CellKey AI"}
          autoFocus={autoFocus}
          className="resize-none border-none outline-none flex-1 min-w-[200px] overflow-y-auto"
          style={{
            background: "transparent",
            minHeight: "24px",
            maxHeight: "380px",
          }}
            onKeyDown={handleKeyDown}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              adjustTextareaHeight();
            }}
            rows={1}
            disabled={isSending || isDisabled}
          />
        </div>

      <div className="flex pl-3 pr-2 pb-2 justify-between shrink-0">
        {icons}
        <div className="w-[40px] h-[40px] flex items-center justify-center">
          {(isThisChatStreaming || isServerStreaming) ? (
            <button
              onClick={() => stopChat(chatId)}
              className="bg-[#FFEAEC] rounded-full text-white w-[28px] h-[28px] flex items-center justify-center hover:bg-primary-60 transition-colors"
            >
              <div className="rounded-[2px] bg-red-30 w-3 h-3" />
            </button>
          ) : (
            sendButton
          )}
        </div>
      </div>

      {isStopChatPending && <Spinner />}
    </div>
  );
};

export default MessageInputMedium;
